import { useEffect, useState, useMemo } from 'react';
import { apiFetch } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, Plus, Trash2, Flower2 } from 'lucide-react';

/**
 * AddLineDialog — two tabs:
 *   1. Catalog: search + pick active product (uses its current approved recipe version + cost snapshot)
 *   2. Custom:  build a quotation-specific draft recipe (ingredients + labor + packaging + selling price)
 */
export default function AddLineDialog({ open, onClose, onAdded, quoteId }) {
  const { role } = useAuth();
  const [tab, setTab] = useState('catalog');
  const canDesignCustom = ['admin','manager','designer'].includes(role);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="add-line-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Add line</DialogTitle></DialogHeader>
        <div className="border-b border-border flex">
          <button onClick={() => setTab('catalog')} className={`px-5 py-2 text-sm border-b-2 ${tab==='catalog' ? 'border-foreground' : 'border-transparent text-muted-foreground'}`} data-testid="add-line-tab-catalog">Catalog</button>
          <button onClick={() => setTab('custom')} className={`px-5 py-2 text-sm border-b-2 ${tab==='custom' ? 'border-foreground' : 'border-transparent text-muted-foreground'}`} data-testid="add-line-tab-custom">Custom arrangement</button>
        </div>
        {tab === 'catalog'
          ? <CatalogPicker quoteId={quoteId} onAdded={() => { onAdded(); onClose(); }} />
          : <CustomForm quoteId={quoteId} onAdded={() => { onAdded(); onClose(); }} disabled={!canDesignCustom && role !== 'sales'} />}
      </DialogContent>
    </Dialog>
  );
}

function CatalogPicker({ quoteId, onAdded }) {
  const [products, setProducts] = useState([]);
  const [state, setState] = useState('loading');
  const [q, setQ] = useState('');
  const [selectedSku, setSelectedSku] = useState(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const list = await apiFetch('/catalog/products');
        // Only products eligible for quoting: not draft, active, priced, with active/approved recipe
        const eligible = (list || []).filter(p => !p.is_draft && p.active_for_sale && p.current_price != null);
        setProducts(eligible);
        setState('ok');
      } catch (e) { toast.error(e.message); setState('error'); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return products;
    return products.filter(p =>
      (p.name || '').toLowerCase().includes(s) ||
      (p.product_sku || '').toLowerCase().includes(s) ||
      (p.collection_name || '').toLowerCase().includes(s)
    );
  }, [products, q]);

  const add = async () => {
    if (!selectedSku) return toast.error('Pick a product first');
    setBusy(true);
    try {
      await apiFetch(`/quotations/${quoteId}/lines/catalog`, {
        method: 'POST',
        body: JSON.stringify({ product_sku: selectedSku, qty: Number(qty) }),
      });
      toast.success('Line added');
      onAdded();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="pt-4 space-y-3">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search products…" className="pl-9 rounded-sm" data-testid="catalog-picker-search" />
      </div>
      <div className="max-h-72 overflow-y-auto border border-border rounded-sm">
        {state === 'loading' && <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>}
        {state === 'ok' && filtered.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No eligible products.</div>}
        {filtered.map(p => (
          <button
            key={p.id}
            onClick={() => setSelectedSku(p.product_sku)}
            className={`w-full text-left px-4 py-3 border-b border-border last:border-b-0 flex items-center gap-3 hover:bg-[hsl(var(--muted))]/60 ${selectedSku===p.product_sku ? 'bg-[hsl(var(--muted))]/80' : ''}`}
            data-testid={`catalog-pick-${p.product_sku}`}
          >
            <div className="w-10 h-10 bg-[hsl(var(--muted))] rounded-sm flex items-center justify-center shrink-0">
              {p.photo_url ? <img src={p.photo_url} alt="" className="w-full h-full object-cover rounded-sm" /> : <Flower2 className="h-4 w-4 text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-sm truncate">{p.name}</div>
              <div className="text-[11px] text-muted-foreground font-mono">{p.product_sku} · {p.collection_name || '—'}</div>
            </div>
            <div className="text-xs velvet-money">${Number(p.current_price).toFixed(2)}</div>
          </button>
        ))}
      </div>
      <div className="flex items-end gap-2 justify-end pt-2">
        <div className="w-24">
          <Label>Qty</Label>
          <Input type="number" min="1" value={qty} onChange={e => setQty(e.target.value)} className="rounded-sm text-right" data-testid="catalog-pick-qty" />
        </div>
        <Button disabled={busy || !selectedSku} onClick={add} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="catalog-add-line">
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

function CustomForm({ quoteId, onAdded, disabled }) {
  const [form, setForm] = useState({
    item_name: '',
    item_description: '',
    qty: 1,
    unit_price: '',
    packaging_cost: 0,
    labor_minutes: 0,
    design_instructions: '',
    ingredients: [],
  });
  const [busy, setBusy] = useState(false);
  const addIng = () => setForm(f => ({ ...f, ingredients: [...f.ingredients, { ingredient_name: '', qty: 1, unit: 'stem', cost_per_unit: '' }] }));
  const updIng = (i, k, v) => setForm(f => ({ ...f, ingredients: f.ingredients.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const rmIng = (i) => setForm(f => ({ ...f, ingredients: f.ingredients.filter((_, j) => j !== i) }));

  const estCost = useMemo(() => {
    let c = 0;
    form.ingredients.forEach(i => {
      if (i.qty && i.cost_per_unit) c += Number(i.qty) * Number(i.cost_per_unit);
    });
    c += Number(form.packaging_cost || 0);
    c += (Number(form.labor_minutes || 0) / 60) * 20; // approx client-side using default $20/hr; server recomputes with authoritative rate
    return c;
  }, [form]);

  const submit = async () => {
    if (!form.item_name.trim()) return toast.error('Name required');
    if (!form.unit_price) return toast.error('Unit price required');
    setBusy(true);
    try {
      await apiFetch(`/quotations/${quoteId}/lines/custom`, {
        method: 'POST',
        body: JSON.stringify({
          item_name: form.item_name,
          item_description: form.item_description || null,
          qty: Number(form.qty),
          unit_price: Number(form.unit_price),
          packaging_cost: Number(form.packaging_cost || 0),
          labor_minutes: Number(form.labor_minutes || 0),
          design_instructions: form.design_instructions || null,
          ingredients: form.ingredients.map(i => ({
            ingredient_sku: i.ingredient_sku || null,
            ingredient_name: i.ingredient_name,
            color: i.color || null,
            qty: Number(i.qty),
            unit: i.unit || null,
            substitutions: i.substitutions || null,
            cost_per_unit: i.cost_per_unit ? Number(i.cost_per_unit) : null,
          })).filter(i => i.ingredient_name),
        }),
      });
      toast.success('Custom line added');
      onAdded();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  if (disabled) return <p className="text-sm text-muted-foreground py-4">Custom recipe authoring requires an Admin, Manager, or Designer.</p>;

  return (
    <div className="pt-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label>Arrangement name*</Label>
          <Input value={form.item_name} onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))} className="rounded-sm" data-testid="custom-name" />
        </div>
        <div className="col-span-2">
          <Label>Description</Label>
          <Textarea rows={2} value={form.item_description} onChange={e => setForm(f => ({ ...f, item_description: e.target.value }))} className="rounded-sm" data-testid="custom-desc" />
        </div>
        <div><Label>Qty</Label><Input type="number" min="1" value={form.qty} onChange={e => setForm(f => ({ ...f, qty: e.target.value }))} className="rounded-sm" data-testid="custom-qty" /></div>
        <div><Label>Unit selling price*</Label><Input type="number" step="0.01" value={form.unit_price} onChange={e => setForm(f => ({ ...f, unit_price: e.target.value }))} className="rounded-sm" data-testid="custom-price" /></div>
        <div><Label>Packaging cost</Label><Input type="number" step="0.01" value={form.packaging_cost} onChange={e => setForm(f => ({ ...f, packaging_cost: e.target.value }))} className="rounded-sm" data-testid="custom-packaging" /></div>
        <div><Label>Labor minutes</Label><Input type="number" value={form.labor_minutes} onChange={e => setForm(f => ({ ...f, labor_minutes: e.target.value }))} className="rounded-sm" data-testid="custom-labor" /></div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Ingredients / materials</Label>
          <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={addIng} data-testid="custom-add-ing"><Plus className="h-3 w-3 mr-1" /> Add</Button>
        </div>
        <div className="space-y-2">
          {form.ingredients.map((i, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_60px_70px_100px_28px] gap-2 items-center" data-testid={`custom-ing-${idx}`}>
              <Input placeholder="Name / SKU" value={i.ingredient_name} onChange={e => updIng(idx, 'ingredient_name', e.target.value)} className="rounded-sm h-8 text-xs" />
              <Input type="number" step="0.1" placeholder="Qty" value={i.qty} onChange={e => updIng(idx, 'qty', e.target.value)} className="rounded-sm h-8 text-xs text-right" />
              <Input placeholder="Unit" value={i.unit || ''} onChange={e => updIng(idx, 'unit', e.target.value)} className="rounded-sm h-8 text-xs" />
              <Input type="number" step="0.01" placeholder="$/unit" value={i.cost_per_unit} onChange={e => updIng(idx, 'cost_per_unit', e.target.value)} className="rounded-sm h-8 text-xs text-right" />
              <button onClick={() => rmIng(idx)} className="p-1 text-muted-foreground hover:text-foreground"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {form.ingredients.length === 0 && <p className="text-xs text-muted-foreground">No ingredients yet.</p>}
        </div>
      </div>

      <div className="text-xs text-muted-foreground border-t border-border pt-3">
        Estimated cost (approx): <span className="velvet-money text-foreground" data-testid="custom-est-cost">${estCost.toFixed(2)}</span>
        <span className="ml-2">Server will recompute with the authoritative labor rate on save.</span>
      </div>

      <DialogFooter className="pt-3">
        <Button disabled={busy} onClick={submit} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="custom-add-line">
          Add custom line
        </Button>
      </DialogFooter>
    </div>
  );
}
